import { Article } from '../articlesData';

export const articleUnderskriftBestyrelsesreferat: Article = {
  id: 'underskrift-bestyrelsesreferat-generalforsamling',
  slug: 'underskrift-bestyrelsesreferat-generalforsamling',
  title: 'Underskrift af bestyrelsesreferat: Regler for tegningsret, digital signatur og Erhvervsstyrelsen',
  subtitle: 'Få overblik over selskabslovens krav til protokollering og underskrift af referater. Sådan håndterer du flere bestyrelsesmedlemmer digitalt uden forsinkelser.',
  metaTitle: 'Bestyrelsesreferat Underskrift: MitID & Selskabsret | KvikSign',
  metaDescription: 'Lær reglerne for digital underskrift af bestyrelsesreferater og generalforsamlinger med MitID. Opfyld selskabsloven og Erhvervsstyrelsens krav nemt og sikkert.',
  canonicalUrl: 'https://kviksign.com/da/articles/underskrift-bestyrelsesreferat-generalforsamling',
  heroImage: 'https://firebasestorage.googleapis.com/v0/b/kviksign-db-europe/o/Website%2FPhotos%2FGeneric%20work%20environment.webp?alt=media&token=ea262c01-9ddd-49b9-bcb5-5b751aa0075f',
  heroImageAlt: 'Bestyrelse underskriver referat og generalforsamlingsprotokol digitalt med MitID',
  category: 'Selskabsledelse & Jura',
  author: {
    name: 'KvikSign',
    role: 'eID & Sikkerhedsteam',
    avatar: '/icon.svg'
  },
  publishedAt: '2026-10-14',
  updatedAt: '2026-10-14',
  readingTime: '6 min læsetid',
  keywords: [
    'bestyrelsesreferat underskrift',
    'selskabsloven bestyrelsesprotokol',
    'generalforsamlingsprotokol digital signatur',
    'erhvervsstyrelsen godkendelse referat',
    'tegningsret digital signering',
    'underskrive med flere parter mitid',
    'sekventiel signering bestyrelse',
    'bestyrelsesmøde protokol mitid'
  ],
  keyTakeaways: [
    'Selskabslovens § 128 fastslår, at forhandlingerne i bestyrelsen skal indføres i en forhandlingsprotokol, der skal underskrives af samtlige tilstedeværende bestyrelsesmedlemmer.',
    'Erhvervsstyrelsen i Danmark og Bolagsverket i Sverige accepterer fuldt ud elektroniske referater og generalforsamlingsbeslutninger underskrevet med eID (MitID / BankID).',
    'Manuel papir-cirkulering mellem bestyrelsesmedlemmer bosat i forskellige byer eller lande forsinker ofte registreringer af kapitalforhøjelser eller vedtægtsændringer i ugevis.',
    'Sekventiel eller parallel digital udsendelse sikrer, at alle bestyrelsesmedlemmer automatisk modtager dokumentet og kan godkende på under 30 sekunder fra enhver enhed.',
    'KvikSign genererer et komplet revisionsspor med kryptografisk tidsstempel, der dokumenterer nøjagtigt, hvornår hvert enkelt medlem signerede protokollen.'
  ],
  tableOfContents: [
    { id: 'hvad-siger-selskabsloven-om-bestyrelsesreferater', title: 'Hvad siger selskabsloven om bestyrelsesreferater?' },
    { id: 'hvem-skal-underskrive-referatet', title: 'Hvem skal underskrive referatet?' },
    { id: 'erhvervsstyrelsens-krav-ved-anmeldelse-og-registrering', title: 'Erhvervsstyrelsens krav ved registrering' },
    { id: 'sekventiel-signering-naar-flere-skal-skrive-under', title: 'Sekventiel signering: Når flere skal skrive under i rækkefølge' },
    { id: 'fordele-ved-mitid-til-bestyrelsesarbejde', title: 'Fordele ved MitID til bestyrelsesarbejde' },
    { id: 'download-skabelon-til-bestyrelsesprotokol', title: 'Download gratis skabelon til bestyrelsesprotokol' },
    { id: 'faq-bestyrelsesreferat', title: 'Ofte stillede spørgsmål' }
  ],
  sections: [
    {
      id: 'hvad-siger-selskabsloven-om-bestyrelsesreferater',
      title: 'Hvad siger selskabsloven om bestyrelsesreferater?',
      paragraphs: [
        'I henhold til selskabslovens § 128 skal bestyrelsen i et kapitalselskab (A/S og ApS med bestyrelse) føre en protokol over sine forhandlinger.',
        'Protokollen tjener som selskabets officielle historiske dokumentation for trufne beslutninger, herunder strategiske retningslinjer, godkendelse af årsrapporter, ansættelse af direktører samt tildeling af fuldmagter.',
        'Loven stiller krav om, at referatet skal underskrives af samtlige de medlemmer, der har deltaget i bestyrelsesmødet. Et bestyrelsesmedlem, som ikke er enig i en beslutning, har ret til at få sin dissens indført i protokollen, men skal stadig underskrive dokumentet.'
      ]
    },
    {
      id: 'hvem-skal-underskrive-referatet',
      title: 'Hvem skal underskrive referatet?',
      paragraphs: [
        'Reglerne for underskrift afhænger af mødets type:',
        '• Ordinære og ekstraordinære bestyrelsesmøder: Skal underskrives af samtlige tilstedeværende bestyrelsesmedlemmer.',
        '• Generalforsamlinger: Skal underskrives af dirigenten for generalforsamlingen (og i visse tilfælde af samtlige kapitalejere, f.eks. ved enstemmig skriftlig generalforsamling uden fremmøde).',
        '• Direktørens deltagelse: Direktøren har ret til at udtale sig og deltage, men har kun pligt til at underskrive, hvis direktøren tillige er valgt som bestyrelsesmedlem.'
      ]
    },
    {
      id: 'erhvervsstyrelsens-krav-ved-anmeldelse-og-registrering',
      title: 'Erhvervsstyrelsens krav ved registrering',
      paragraphs: [
        'Når der foretages ændringer i selskabets forhold på Virk.dk (f.eks. valg af ny direktion, ændring af revisor, vedtægtsændringer eller kapitalforhøjelse), skal det relevante referat uploades som bilag til Erhvervsstyrelsen.',
        'Erhvervsstyrelsen har eksplicit bekræftet, at PDF-dokumenter med digitale signaturer (AES) er fuldt acceptable. Det er afgørende, at PDF\'en ikke "flades ud" eller printes og scannes igen, da det digitale kryptografiske certifikat derved brydes.'
      ]
    },
    {
      id: 'sekventiel-signering-naar-flere-skal-skrive-under',
      title: 'Sekventiel signering: Når flere skal skrive under i rækkefølge',
      paragraphs: [
        'Når et referat skal underskrives af 3, 5 eller 7 bestyrelsesmedlemmer, kan processen enten køre parallelt (alle inviteres samtidig) eller sekventielt (i en fastlagt rækkefølge, f.eks. formand først, derefter øvrige medlemmer).',
        'KvikSign håndterer automatisk påmindelser og adviseringer. Du kan i realtid følge med i, hvem der har åbnet dokumentet, og hvem der mangler at signere.'
      ]
    },
    {
      id: 'fordele-ved-mitid-til-bestyrelsesarbejde',
      title: 'Fordele ved MitID til bestyrelsesarbejde',
      paragraphs: [
        '• Intet papirarbejde: Bestyrelsesmedlemmer kan sidde i toget eller i udlandet og underskrive på deres mobil med MitID eller BankID.',
        '• Høj sikkerhed: Ingen risiko for forfalskede underskrifter.',
        '• Øjeblikkelig færdiggørelse: Revisionsgodkendte referater er klar til anmeldelse på under én time efter mødets afslutning.'
      ]
    },
    {
      id: 'download-skabelon-til-bestyrelsesprotokol',
      title: 'Download gratis skabelon til bestyrelsesprotokol',
      paragraphs: [
        'Hent vores juridiske standard-skabelon til bestyrelsesreferat, der indeholder standardpunkter for konstituering, dagsorden, beslutningsprotokol og afstemningsresultater.'
      ]
    }
  ],
  faqs: [
    {
      question: 'Kan et bestyrelsesmedlem underskrive med personligt MitID i stedet for erhvervs-MitID?',
      answer: 'Ja. Et bestyrelsesmedlem er udpeget som fysisk person, og et personligt MitID er fuldt lovligt og anerkendt til at identificere underskriveren.'
    },
    {
      question: 'Hvad sker der, hvis et medlem nægter at skrive under?',
      answer: 'Medlemmet har pligt til at underskrive, men har ret til at få sin afvigende mening (dissens) ført til protokols. Nægter medlemmet alligevel, skal dette noteres i protokollen.'
    },
    {
      question: 'Accepterer Erhvervsstyrelsen KvikSigns digitale signaturer?',
      answer: 'Ja, KvikSigns signaturer er fuldt eIDAS-kompatible og overholder Erhvervsstyrelsens standarder for elektronisk bilagsdokumentation.'
    }
  ],
  sources: [
    { title: 'Selskabsloven § 128 (Retsinformation)', url: 'https://www.retsinformation.dk' },
    { title: 'Erhvervsstyrelsen - Digitale anmeldelser på Virk', url: 'https://erhvervsstyrelsen.dk' }
  ],
  disclaimer: 'Denne artikel yder generel orientering om selskabsretlige regler og referatføring og udgør ikke juridisk eller revisionsmæssig rådgivning.'
};
